/** CAS registry numbers: 2–7 digits, 2 digits, 1 check digit. */
const CAS_SHAPE = /^\d{2,7}-\d{2}-\d$/;
/** Registry and database codes (UNII-…, CHEBI:…, DB00945, NSC 27223, EINECS 200-064-1). */
const CODE_LIKE = /\d{3,}|:|^[A-Z0-9-]{4,}$/;

/**
 * ADR 0017 D1 (#906): "Also known as" -- a few names a person would recognise, from PubChem's
 * synonyms. PubChem lists registry numbers, database codes and spelling variants among them, so
 * those are left out (CAS RN has its own row), as are repeats of the title.
 */
export function readableSynonyms(synonyms: readonly string[], title: string | null, limit = 3): string[] {
  const titleKey = title?.toLowerCase();
  // First appearance sets the order; a normal-case spelling replaces an ALL-CAPS one of the same name.
  const byKey = new Map<string, string>();
  for (const raw of synonyms) {
    const name = raw.trim();
    const key = name.toLowerCase();
    if (!name || name.length > 40 || key === titleKey || CAS_SHAPE.test(name) || CODE_LIKE.test(name)) continue;
    const kept = byKey.get(key);
    if (kept === undefined || (kept === kept.toUpperCase() && name !== name.toUpperCase())) byKey.set(key, name);
  }
  return [...byKey.values()].slice(0, limit);
}

/** PubChem's compound page, or one of its sections ("Literature", "Patents"). */
export function pubChemUrl(cid: number | string, section?: string): string {
  const base = `https://pubchem.ncbi.nlm.nih.gov/compound/${encodeURIComponent(String(cid))}`;
  return section ? `${base}#section=${encodeURIComponent(section)}` : base;
}

/**
 * #908: the standard UN GHS wording for a hazard code -- ours, never the source's. A combined code
 * ("H302+H312") joins its parts; null when any part is unknown, so the page shows the bare code
 * rather than a half-translated statement.
 */
export function ghsWording(code: string, wordingOf: (part: string) => string | undefined): string | null {
  const parts = code.split('+').map((part) => wordingOf(part.trim()));
  return parts.every((part): part is string => Boolean(part)) ? parts.join('; ') : null;
}

/**
 * #910 (ADR 0017 D11): a stable key for a factsheet statement -- FNV-1a (64-bit) of its source and
 * text -- so a comment can point at it, and the page can scroll back to it, without an id from the
 * server. Statements become ADR 0018 assertions later, with ids of their own.
 */
export function statementKey(source: string, text: string): string {
  let hash = 0xcbf29ce484222325n;
  const input = `${source}\u0000${text}`;
  for (let index = 0; index < input.length; index++) {
    hash ^= BigInt(input.charCodeAt(index));
    hash = (hash * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return `s-${hash.toString(16).padStart(16, '0')}`;
}
